<script setup lang="ts">
import { useToggleStore } from '@/store/toggle/toggle'

import {
  HttpStatusCode,
  NotFoundError,
  ServerError,
  Success,
} from '@/api/results'
import { SliderType } from '@/types/api/api-v4/slider'

import LandingMain from '@/components/Main/Landing/LandingMain.vue'
import TheMain from '@/components/Main/TheMain.vue'

const { isDesktop } = useDevice()

const { isLandingPageEnabled } = useToggleStore()

const { data: seoContent } = await useAsyncData(async (nuxtApp) => {
  const result = await nuxtApp?.$apiService.main.fetchMainPage()

  if (result instanceof Success) return result.data

  return null
})

const { data, error: isSlidersError } = await useAsyncData(async (nuxtApp) => {
  const result = await nuxtApp?.$apiService.category.fetchMainSliders()

  if (result instanceof NotFoundError) {
    throw createError({
      statusCode: HttpStatusCode.NOT_FOUND,
    })
  }

  if (result instanceof ServerError) {
    throw createError({
      statusCode: HttpStatusCode.INTERNAL_SERVER_ERROR,
    })
  }

  return result?.data
})

if (isSlidersError.value) showError(isSlidersError.value)

const getSlidersByType = (type: SliderType) => {
  return data.value?.filter((slider) => slider?.type === type) || []
}

const slidersList = {
  sliders: getSlidersByType(SliderType.SIMPLE),
  saleSlider: getSlidersByType(SliderType.SALE),
  popularSlider: getSlidersByType(SliderType.POPULAR),
}

usePopupFetch()
</script>

<template>
  <div :class="{ 'base-container': isDesktop }">
    <app-head
      :title="seoContent?.seo?.seo_title"
      :seo="{
        title: seoContent?.seo?.seo_title,
        description: seoContent?.seo?.seo_description,
        keywords: seoContent?.seo?.seo_keywords,
      }"
    />

    <the-main
      v-if="!isLandingPageEnabled"
      :seo-content="seoContent?.content"
      :sliders="slidersList"
    />

    <landing-main
      v-else
      :sliders="slidersList"
      :seo-content="seoContent?.content"
    />
  </div>
</template>

<style lang="scss" scoped>
.base-container {
  padding: 40px calc(50% - 600px) 100px;

  @include media('<=desktop') {
    padding: 40px 24px;
  }

  &__section {
    margin-bottom: 30px;
  }
}
</style>
